import { Injectable, Optional } from '@angular/core';
import { TimelineHistoricService } from './timeline-historic.service';
import { TimelineViewStore } from '../state/timeline-view.store';
import {
  catchError,
  defer,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  AppointmentService,
  DocumentService,
  EmailDocumentService,
  InteractionLogService,
  NoteService,
  SessionService,
  TaskService,
} from '@maximizer/core/shared/data-access';
import {
  ActivityTimeline,
  ActivityTimelineUpcomingOverdue,
  EntityType,
  ListItem,
  Octopus,
  TimelineConfiguration,
  TimelineEvent,
  TimelineHistoricDetailsEvent,
  TimelineHistoricPagination,
  User,
} from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { ActivityTimelineService } from './activity-timeline.service';
import { clampArray } from '@maximizer/shared/util';
import { trackTimelineException } from '../helpers/track-timeline-exception.helper';
import { TimelineDeleteEntry, TimelineKeys } from '../state/timeline.models';

@Injectable()
export class TimelineViewService {
  static readonly PageSize = 20;

  private _historicNextPageKey?: string;
  private _historicNextPageEntryKey?: string;
  private _historicsLoadId = 0;
  private _upcomingOverdueLoadId = 0;
  private _upcomingTop?: number;
  private _showMoreLoadId?: number;

  constructor(
    private readonly timelineHistoricService: TimelineHistoricService,
    private readonly activityService: ActivityTimelineService,
    private readonly store: TimelineViewStore,
    private readonly sessionService: SessionService,
    private readonly appointmentService: AppointmentService,
    private readonly taskService: TaskService,
    private readonly noteService: NoteService,
    private readonly documentService: DocumentService,
    private readonly emailDocumentService: EmailDocumentService,
    private readonly interactionLog: InteractionLogService,
    @Optional() private readonly insights: InsightsService | null,
  ) {}

  initialLoad(excludeInternalServiceUsers = false): Observable<{
    currentUser: User | undefined;
    configuration: TimelineConfiguration | null;
    users: User[];
  }> {
    if (this.store.currentUser() && this.store.timelineConfiguration()) {
      return of({
        currentUser: this.store.currentUser(),
        configuration: this.store.timelineConfiguration(),
        users: this.store.users(),
      });
    }

    return forkJoin({
      currentUser: this.sessionService.getCurrentUser(),
      configuration: this.timelineHistoricService.getConfiguration(),
      users: this.sessionService.getUsersByPreference(
        excludeInternalServiceUsers,
      ),
    }).pipe(
      tap((data) => {
        this.store.setCurrentUser(data.currentUser);
        this.store.setUsers(data.users);
        this.store.setTimelineFilter({
          ...this.store.searchFilter(),
          users: data.users.map((x) => x.id),
        });

        if (data.configuration) {
          this.store.setTimelineConfiguration(data.configuration);
        }
      }),
    );
  }

  private loadHistoricsWithPagination(
    nextPageKey?: string,
  ): Observable<TimelineEvent[]> {
    const clearStore = !nextPageKey;
    if (clearStore) {
      this._historicsLoadId++;
      this._historicNextPageKey = undefined;
    }
    const loadId = this._historicsLoadId;
    const entryKey = this.store.entryKey();

    return this.timelineHistoricService
      .forEntryWithPaginationKey(
        this.store.entryTimelineKeys() ?? this.store.entryKey(),
        this.store.entryType(),
        this.store.searchFilter(),
        nextPageKey,
        TimelineViewService.PageSize,
      )
      .pipe(
        map((result) => {
          if (
            loadId !== this._historicsLoadId ||
            entryKey !== this.store.entryKey()
          ) {
            return this.store.timelineEvents();
          }
          this._historicNextPageKey = result.nextPageKey;
          this._historicNextPageEntryKey = entryKey;
          this.store.setTimelineEvents(
            clearStore
              ? result.data
              : [...this.store.timelineEvents(), ...result.data],
          );
          return this.store.timelineEvents();
        }),
      );
  }

  hasHistoricNextPageKey(): boolean {
    return !!this.historicNextPageKeyForEntry();
  }

  private historicNextPageKeyForEntry(): string | undefined {
    return this._historicNextPageEntryKey === this.store.entryKey()
      ? this._historicNextPageKey
      : undefined;
  }

  loadHistorics(): Observable<TimelineEvent[]> {
    return this.loadHistoricsWithPagination();
  }

  loadHistoricByKey(key: string): Observable<TimelineHistoricPagination> {
    return this.timelineHistoricService.getEntryByKey(key);
  }

  loadHistoricNextPage(): Observable<TimelineEvent[]> {
    const nextPageKey = this.historicNextPageKeyForEntry();
    if (nextPageKey) {
      return this.loadHistoricsWithPagination(nextPageKey);
    }
    return of(this.store.timelineEvents());
  }

  getLeadAndAbEntryKeyFrom(opportunityKey: string): Observable<TimelineKeys> {
    return this.timelineHistoricService.getLeadAndAbEntryKeyFrom(
      opportunityKey,
    );
  }

  loadUpcomingOverdueByKey(
    key: string,
  ): Observable<ActivityTimelineUpcomingOverdue> {
    return this.activityService.getOverdueAndUpcomingByKey(key);
  }

  loadUpcomingOverdue(top?: number): Observable<ActivityTimeline[]> {
    const loadId = ++this._upcomingOverdueLoadId;
    const entryKey = this.store.entryKey();

    // top +1 so we can know if there are more
    // entries to load, so we display: SHOW MORE button.
    return this.activityService
      .getOverdueAndUpcoming(
        this.store.entryTimelineKeys() ?? this.store.entryKey(),
        this.store.entryType(),
        this.store.searchFilter(),
        top ? top + 1 : top,
      )
      .pipe(
        map((result) => {
          if (
            loadId !== this._upcomingOverdueLoadId ||
            entryKey !== this.store.entryKey()
          ) {
            return this.store.activities();
          }
          const clampedUpcoming = top
            ? clampArray(result.upcoming, top)
            : result.upcoming;
          const activities = [...result.overdue, ...clampedUpcoming].sort(
            (a, b) => {
              const aTime = new Date(a.date.value).getTime();
              const bTime = new Date(b.date.value).getTime();
              return aTime - bTime;
            },
          );

          this._upcomingTop = top;
          this.store.setActivities(activities);
          this.store.setCanShowMoreUpcoming(
            !!top && result.upcoming.length > top,
          );
          return this.store.activities();
        }),
      );
  }

  showMoreUpcoming(): Observable<ActivityTimeline[]> {
    return defer(() => {
      if (this._showMoreLoadId === this._upcomingOverdueLoadId) {
        return of(this.store.activities());
      }
      const entryKey = this.store.entryKey();
      const endLoading = this.store.beginLoading();
      const showMore = this.loadUpcomingOverdue();
      const loadId = this._upcomingOverdueLoadId;
      this._showMoreLoadId = loadId;

      return showMore.pipe(
        catchError((error: unknown) => {
          trackTimelineException(
            this.insights,
            error,
            'TimelineViewService',
            'showMoreUpcoming',
          );
          if (
            loadId === this._upcomingOverdueLoadId &&
            entryKey === this.store.entryKey()
          ) {
            this.store.failedAction().next('showMore');
          }
          return of(this.store.activities());
        }),
        finalize(() => {
          endLoading();
          if (this._showMoreLoadId === loadId) {
            this._showMoreLoadId = undefined;
          }
        }),
      );
    });
  }

  deleteEntries(entries: TimelineDeleteEntry[]): Observable<boolean> {
    return defer(() => {
      const requests = this.getDeleteRequests(entries);
      if (requests.length === 0) {
        return of(true);
      }
      const keys = entries.map(({ key }) => key);
      const entryKey = this.store.entryKey();
      const endLoading = this.store.beginLoading();

      return forkJoin(requests).pipe(
        map((results) => results.every(Boolean)),
        catchError((error: unknown) => {
          trackTimelineException(
            this.insights,
            error,
            'TimelineViewService',
            'deleteEntries',
          );
          return of(false);
        }),
        switchMap((deleted) => {
          if (entryKey !== this.store.entryKey()) {
            return of(deleted);
          }
          if (!deleted) {
            this.store.failedAction().next('delete');
            return this.reloadAfterFailedDelete();
          }
          return this.reloadUpcomingOverdueAfterDelete(keys).pipe(
            map(() => {
              this.store.deleteActivities(keys);
              this.store.deleteTimelineEvents(keys);
              return true;
            }),
          );
        }),
        finalize(endLoading),
      );
    });
  }

  private reloadAfterFailedDelete(): Observable<boolean> {
    return forkJoin([
      this.loadHistorics(),
      this.loadUpcomingOverdue(this._upcomingTop),
    ]).pipe(
      catchError((error: unknown) => {
        trackTimelineException(
          this.insights,
          error,
          'TimelineViewService',
          'reloadAfterFailedDelete',
        );
        return of(null);
      }),
      map(() => false),
    );
  }

  private getDeleteRequests(
    entries: TimelineDeleteEntry[],
  ): Observable<boolean>[] {
    type DeleteRequest = (keys: string[]) => Observable<boolean>;
    const deleteByType = new Map<number, DeleteRequest>([
      [
        Octopus.InteractionCode.APPOINTMENT,
        (keys) => this.deleteAppointment(keys),
      ],
      [Octopus.InteractionCode.TASK, (keys) => this.deleteTask(keys)],
      [Octopus.InteractionCode.NOTE, (keys) => this.deleteNote(keys)],
      [Octopus.InteractionCode.DOCUMENT, (keys) => this.deleteDocument(keys)],
      [Octopus.InteractionCode.EMAIL, (keys) => this.deleteEmailDocument(keys)],
    ]);
    const deleteInteractionLog: DeleteRequest = (keys) =>
      this.deleteInteractionLog(keys);

    const keysByRequest = new Map<DeleteRequest, string[]>();
    entries.forEach(({ key, interactionType }) => {
      const request = deleteByType.get(interactionType) ?? deleteInteractionLog;
      keysByRequest.set(request, [...(keysByRequest.get(request) ?? []), key]);
    });
    return [...keysByRequest].map(([request, keys]) => request(keys));
  }

  reloadUpcomingOverdueAfterDelete(
    deletedKeys: string[],
  ): Observable<ActivityTimeline[]> {
    const sectionRowDeleted = this.store
      .activities()
      .some(({ key }) => key !== null && deletedKeys.includes(key));
    if (!this.store.canShowMoreUpcoming() || !sectionRowDeleted) {
      return of(this.store.activities());
    }

    return this.loadUpcomingOverdue(this._upcomingTop).pipe(
      catchError((error: unknown) => {
        trackTimelineException(
          this.insights,
          error,
          'TimelineViewService',
          'reloadUpcomingOverdueAfterDelete',
        );
        return of(this.store.activities());
      }),
    );
  }

  loadHistoricDetails(
    key: string,
    type: EntityType,
  ): Observable<TimelineHistoricDetailsEvent | null> {
    return this.timelineHistoricService.forEntryDetails(key, type);
  }

  getNoteFieldOptions(parentKey: string): Observable<ListItem<string>[]> {
    return this.noteService.getNoteFieldOptions(parentKey);
  }

  getAbEntryOrContactKeyFrom(caseKey: string): Observable<TimelineKeys> {
    return this.timelineHistoricService.getAbEntryOrContactKeyFrom(caseKey);
  }

  private deleteAppointment(keys: string[]): Observable<boolean> {
    return this.appointmentService.delete(keys);
  }

  private deleteTask(keys: string[]): Observable<boolean> {
    return this.taskService.delete(keys);
  }

  private deleteNote(keys: string[]): Observable<boolean> {
    return this.noteService.delete(keys);
  }

  private deleteDocument(keys: string[]): Observable<boolean> {
    return this.documentService.delete(keys);
  }

  private deleteEmailDocument(keys: string[]): Observable<boolean> {
    return this.emailDocumentService.delete(keys);
  }

  private deleteInteractionLog(keys: string[]): Observable<boolean> {
    return this.interactionLog.delete(keys);
  }
}
